import _ from 'lodash';
import { createSelector } from 'reselect';
import {
  extractSettingAs,
  calculateAssignmentsTotals,
} from '@macmillan-learning/gradebook-shared';
import { sortAssignmentsByISO } from '../utils/util';
import { getGradebookGridDataSelector, getCategoriesRow } from './gradebook';
import { GRADING_TYPE } from '../utils/constants';

const getGradebookCategories = (state) => state.gradebook.categories;
const getGradebookCategoryWeights = (state) => state.gradebook.categoryWeights;
const getGradebookAverages = (state) => state.gradebook.averages;
const getGradebookSettings = (state) => state.gradebook.settings;
const getCollapsedCategoryIds = (state) => state.gradebook.collapsedCategoryIds;
const getGradebookAssignments = (state) => state.gradebook.assignments;

export const getCategoryAssignments = (category, courseAssignments) => {
  // eslint-disable-next-line no-param-reassign
  courseAssignments = courseAssignments || [];
  const foundAssignments = _.filter(courseAssignments, (ass) => {
    const gradebookCategory = ass.gradebook_category_id
      ? ass.gradebook_category_id
      : 0;
    return gradebookCategory === category.id;
  });
  return foundAssignments;
};

export const getStudentCategoryAssignmentsWithAggregate = (
  category,
  courseAssignments,
  getCategoryAssignmentsFn,
) => {
  const categoryAssignments = _.sortBy(
    getCategoryAssignmentsFn(category, courseAssignments),
    'name',
  );
  categoryAssignments.forEach((cat) => {
    cat.drop_lowest_num_grades = category.drop_lowest_num_grades;
  });
  const unfilteredCategoryAssignments = getCategoryAssignmentsFn(
    category,
    courseAssignments,
  );
  const notDroppedAssignments = _.filter(
    unfilteredCategoryAssignments,
    (o) => !o.isDropped,
  );
  const { pointsEarned, pointsPossible, grade } = calculateAssignmentsTotals(
    notDroppedAssignments,
  );
  // This has to be pushed on the end of the array to render as the last column
  const aggregateAssignment = {
    id: `${category.id}`,
    name: 'Category Total',
    studentPoints: pointsEarned,
    assigned_points: pointsPossible,
    gradebook_category_id: category.id,
    gradebook_category_name: category.name,
    isAggregateCell: true,
    studentGrade: grade,
  };
  const sortedNonAggregate = sortAssignmentsByISO(categoryAssignments);
  sortedNonAggregate.push(aggregateAssignment);

  return sortedNonAggregate;
};

export const getStudentCategoryAssignments = (category, student) =>
  getStudentCategoryAssignmentsWithAggregate(
    category,
    student,
    getCategoryAssignments,
  );

const getAssignmentsRow = (
  categories,
  _requireStudentId,
  courseAssignments,
) => {
  const categoriesWithAssignments = _.flatten(
    _.map(categories, (cat) =>
      getStudentCategoryAssignments(cat, courseAssignments),
    ),
  );
  const attachCategoryName = (arrayCategoriesWithAssignments) =>
    arrayCategoriesWithAssignments.map((item) => {
      if (!item.gradebook_category_name && item.gradebook_category_id) {
        const categoryName =
          _.find(categories, { id: item.gradebook_category_id }).name || '';
        return {
          ...item,
          gradebook_category_name: categoryName,
        };
      }

      return item;
    });

  // Always returns at least one for the default assignments category
  const hasAssignments =
    courseAssignments.length &&
    Array.isArray(categoriesWithAssignments) &&
    categoriesWithAssignments.length > 1;

  return {
    assignmentsRow: attachCategoryName(categoriesWithAssignments),
    hasAssignments,
  };
};

export const getAveragesRow = (
  categories,
  leftGridColumnCount,
  courseAssignments,
) => {
  const allCatAverages = _.flatten(
    _.map(categories, (cat) =>
      getStudentCategoryAssignments(cat, courseAssignments),
    ),
  );
  const columnsWithAverages = allCatAverages.map((asgn) => {
    if (!asgn.isAggregateCell) {
      const averageData = courseAssignments.find((avg) => avg.id === asgn.id);
      const mappedAverageData = {
        ...averageData,
        pointsEarned: '-',
        pointsPossible: averageData.assigned_points,
      };
      return {
        ...mappedAverageData,
        gradebook_category_id: asgn.gradebook_category_id,
        missing: !averageData,
      };
    }
    return asgn;
  });

  for (let i = 0; i < leftGridColumnCount - 1; i++) {
    columnsWithAverages.unshift({ isEmptyCell: true });
  }

  return columnsWithAverages;
};

const getStudentData = (averagesRow) => {
  const studentData = [];
  averagesRow.forEach((average) => {
    studentData.push({
      missing: !average.isAggregateCell,
      blank: average.isAggregateCell,
    });
  });
  return studentData;
};

// buildGradebookGridData builds the data structure for the instructor gradebook grid
// It's an array of arrays
// Each array withing the array is considered a row.
// Each array row contains 0 or more objects
// Those objects are the data for each cell within the table
// [
//    [{celldata}, {celldata}...]
//    [{celldata}, {celldata}...]
// ]
const buildGradebookGridData = (
  categories,
  weights,
  _averages,
  requireStudentId,
  courseAssignments,
) => {
  const leftGridColumnCount = 0;
  const isStudentIdRequired = requireStudentId === 'true';
  const rows = [];
  const courseAssignmentsWithGradingType = _.map(courseAssignments, (a) => ({
    ...a,
    isUngraded: a.grading_type === GRADING_TYPE.UNGRADED,
  }));
  const { assignmentsRow, hasAssignments } = getAssignmentsRow(
    categories,
    requireStudentId,
    courseAssignmentsWithGradingType,
  );
  const categoryRow = getCategoriesRow(
    assignmentsRow,
    weights,
    leftGridColumnCount,
  );
  const averagesRow = getAveragesRow(
    categories,
    leftGridColumnCount,
    courseAssignmentsWithGradingType,
  );
  const columnCount = _.get(rows, '[1].length', 0);
  const studentData = getStudentData(averagesRow);
  const isGradebookEmpty = !hasAssignments;

  rows.unshift(categoryRow, assignmentsRow, averagesRow, studentData);

  return {
    rows,
    rowCount: 1,
    columnCount,
    leftGridColumnCount,
    isStudentIdRequired,
    hasAssignments,
    isGradebookEmpty,
    hasStudents: false,
  };
};

// WARNING: This operation is expensive and should
// only recompute when absolutely necessary
const getGradebookRows = createSelector(
  [
    getGradebookCategories,
    getGradebookCategoryWeights,
    getGradebookAverages,
    getGradebookSettings,
    getGradebookAssignments,
  ],
  (categories, weights, averages, settings, courseAssignments) => {
    const requireStudentId = extractSettingAs(
      settings,
      'requireStudentId',
      ({ value }) => value,
    );
    return buildGradebookGridData(
      categories,
      weights,
      averages,
      requireStudentId,
      courseAssignments,
    );
  },
);

// This selector will only recompute when the
// following changes happen and will not recompute getGradebookRows
// - gradebookGridData
// - collapsedCategoryIds
const getEmptyGradebookGridData = createSelector(
  [getGradebookRows, getCollapsedCategoryIds],
  (gradebookGridData, collapsedCategoryIds) =>
    getGradebookGridDataSelector(gradebookGridData, collapsedCategoryIds),
);

export default getEmptyGradebookGridData;
